import { useState, useMemo } from 'react';
import {
  Pencil,
  Plus,
  Lock,
  Calendar,
  Clock,
  Trash2,
  Search,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/ui/error-boundary';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { EmployeeDetail } from '@/types/employee';
import type { EmployeeNote } from '@/types/employee-note';
import {
  useEmployeeNotes,
  useCreateEmployeeNote,
  useUpdateEmployeeNote,
  useDeleteEmployeeNote,
} from '@/hooks/use-employee-notes';
import { useTags } from '@/hooks/use-tags';
import { IconActionButton } from './icon-action-button';
import { EmployeeNoteDialog } from './employee-note-dialog';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/date';
import { sanitizeHTML } from '@/lib/sanitize-html';
import { usePermission } from '@/lib/permissions';
import { useEmployeeNameFormatter } from '@/lib/employee-name';

interface NotesTabProps {
  employee: EmployeeDetail;
}

export function NotesTab({ employee }: NotesTabProps) {
  const { data: notes, isLoading, error } = useEmployeeNotes(employee.id);
  const { data: tags = [] } = useTags();
  const formatName = useEmployeeNameFormatter();
  const canCreateNote = usePermission('employees.notes.create');
  const canEditNote = usePermission('employees.notes.edit');
  const canDeleteNote = usePermission('employees.notes.delete');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<EmployeeNote | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<EmployeeNote | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const createMutation = useCreateEmployeeNote(employee.id);
  const updateMutation = useUpdateEmployeeNote(employee.id, selectedNote?.id || '');
  const deleteMutation = useDeleteEmployeeNote(employee.id);

  // Filter and search logic
  const filteredNotes = useMemo(() => {
    let filtered = notes || [];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((note) => {
        const titleMatch = note.title?.toLowerCase().includes(query);
        const contentMatch = note.content?.toLowerCase().includes(query);
        const authorMatch = note.author
          ? formatName({ firstName: note.author.firstName, lastName: note.author.lastName }).toLowerCase().includes(query) ||
            `${note.author.firstName} ${note.author.lastName}`.toLowerCase().includes(query)
          : false;
        return titleMatch || contentMatch || authorMatch;
      });
    }

    // Filter by selected tags
    if (selectedTagIds.length > 0) {
      filtered = filtered.filter((note) => {
        if (!note.tags || note.tags.length === 0) return false;
        return selectedTagIds.some((tagId) => note.tags.some((noteTag) => noteTag.id === tagId));
      });
    }

    return filtered;
  }, [notes, searchQuery, selectedTagIds, formatName]);

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTagIds([]);
  };

  const hasActiveFilters = searchQuery.trim() !== '' || selectedTagIds.length > 0;

  const handleAddNote = () => {
    setSelectedNote(null);
    setDialogOpen(true);
  };

  const handleEditNote = (note: EmployeeNote) => {
    setSelectedNote(note);
    setDialogOpen(true);
  };

  const handleSaveNote = async (data: {
    title?: string;
    content: string;
    isPrivate: boolean;
    tagIds?: string[];
  }) => {
    if (selectedNote) {
      await updateMutation.mutateAsync(data);
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDeleteNote = async () => {
    if (noteToDelete) {
      await deleteMutation.mutateAsync(noteToDelete.id);
      setNoteToDelete(null);
    }
  };

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return `${diffWeeks}w ago`;
  };

  const getAuthorInitials = (note: EmployeeNote) => {
    if (!note.author) return 'HR';
    const first = note.author.firstName?.charAt(0) || '';
    const last = note.author.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  const getAuthorName = (note: EmployeeNote) => {
    if (!note.author) return 'HR System';
    return formatName({ firstName: note.author.firstName, lastName: note.author.lastName });
  };

  const getAuthorRole = (note: EmployeeNote) => {
    return note.author?.position?.title || 'HR Team';
  };

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            HR Notes
          </h3>
        </div>
        <div className="p-5">
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            HR Notes
          </h3>
        </div>
        <div className="p-5">
          <QueryError error={error} />
        </div>
      </div>
    );
  }

  const notesList = notes || [];
  const hasNotes = notesList.length > 0;
  const hasFilteredNotes = filteredNotes.length > 0;

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border/50 bg-muted/30 px-5 py-3">
          <div className="flex items-center gap-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              HR Notes
            </h3>
            {hasNotes && (
              <span className="rounded-full bg-hygge/10 px-2 py-0.5 text-[10px] font-medium text-hygge">
                {notesList.length} {notesList.length === 1 ? 'note' : 'notes'}
              </span>
            )}
          </div>
          {canCreateNote && (
            <button
              type="button"
              className="flex h-7 items-center gap-1.5 rounded-md bg-hygge/10 px-2.5 text-xs font-medium text-hygge transition-colors hover:bg-hygge/20 cursor-pointer"
              onClick={handleAddNote}
            >
              <Plus className="h-3 w-3" />
              Add Note
            </button>
          )}
        </div>

        <div className="p-5">
          {!hasNotes ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-hygge/10 mb-3">
                <Pencil className="h-4.5 w-4.5 text-hygge" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">No notes yet</p>
              <p className="text-xs text-muted-foreground mb-4">
                Add internal notes visible only to the HR team.
              </p>
              {canCreateNote && (
                <button
                  type="button"
                  className="flex h-8 items-center gap-1.5 rounded-md bg-hygge/10 px-3 text-xs font-medium text-hygge transition-colors hover:bg-hygge/20 cursor-pointer"
                  onClick={handleAddNote}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add First Note
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search notes by title, content, or author..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
                  />
                </div>
              </div>

              {/* Tag Filters */}
              {tags.length > 0 && (
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Filter by Tags
                    </h4>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Clear filters
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => {
                      const isSelected = selectedTagIds.includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => toggleTag(tag.id)}
                          className={cn(
                            'inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer',
                            'hover:scale-105 active:scale-95',
                            isSelected ? 'shadow-md' : 'hover:shadow-sm'
                          )}
                          style={{
                            backgroundColor: isSelected ? tag.color : `${tag.color}15`,
                            color: isSelected ? '#fff' : tag.color,
                            border: `1px solid ${tag.color}${isSelected ? '' : '40'}`,
                            boxShadow: isSelected ? `0 0 0 2px ${tag.color}40` : undefined,
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Results Info */}
              {hasActiveFilters && (
                <div className="mb-3 text-xs text-muted-foreground">
                  Showing {filteredNotes.length} of {notesList.length} note
                  {notesList.length === 1 ? '' : 's'}
                </div>
              )}

              {/* Notes List */}
              {!hasFilteredNotes && hasActiveFilters ? (
                <div className="py-12 text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
                    <Search className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <h3 className="text-sm font-medium text-foreground mb-1">No notes found</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    Try adjusting your search or filters
                  </p>
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear filters
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredNotes.map((note) => (
                  <div
                    key={note.id}
                    className="group animate-fade-in overflow-hidden rounded-xl border border-border bg-card hover:bg-card/80 transition-all hover:shadow-lg"
                  >
                    <div className="p-4">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {note.title && (
                              <h4 className="font-semibold text-foreground">{note.title}</h4>
                            )}
                            {note.isPrivate && (
                              <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium bg-purple-500/10 text-purple-400">
                                <Lock className="h-3 w-3" />
                                Private
                              </span>
                            )}
                          </div>
                        </div>
                        {(canEditNote || canDeleteNote) && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {canEditNote && (
                              <IconActionButton
                                icon={Pencil}
                                tooltip="Edit note"
                                onClick={() => handleEditNote(note)}
                                size="sm"
                              />
                            )}
                            {canDeleteNote && (
                              <IconActionButton
                                icon={Trash2}
                                tooltip="Delete note"
                                onClick={() => setNoteToDelete(note)}
                                variant="destructive"
                                size="sm"
                              />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div
                        className="text-sm text-muted-foreground leading-relaxed mb-4 note-content"
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(note.content) }}
                      />

                      {/* Tags */}
                      {note.tags && note.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {note.tags.map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor: `${tag.color}20`,
                                color: tag.color,
                                border: `1px solid ${tag.color}40`,
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Footer */}
                      <div className="flex items-center gap-4 pt-3 border-t border-border/30">
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              'h-7 w-7 rounded-md flex items-center justify-center text-xs font-semibold',
                              note.author
                                ? 'bg-hygge/15 text-hygge'
                                : 'bg-muted text-muted-foreground'
                            )}
                          >
                            {getAuthorInitials(note)}
                          </div>
                          <div className="text-xs">
                            <span className="font-medium text-foreground">
                              {getAuthorName(note)}
                            </span>
                            <span className="text-muted-foreground"> · {getAuthorRole(note)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground ml-auto">
                          <Calendar className="h-3.5 w-3.5" />
                          <span>
                            {formatDateTime(note.createdAt, {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" />
                          <span>{formatRelativeTime(note.updatedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CustomBlocksSection section="NOTES" employeeId={employee.id} />

      {/* Add/Edit Note Dialog */}
      <EmployeeNoteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        note={selectedNote}
        onSave={handleSaveNote}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!noteToDelete} onOpenChange={() => setNoteToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this note? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

